import { useCallback, useMemo, useState } from "react";
import { MailCheck, Search, UserMinus, UserRoundCheck } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTimeWithZone } from "../../utils/timezone";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { useServerPagination } from "./useServerPagination";
import { ToastStack, useToast } from "./useToast";

const emptyPayload = {
  subscribers: [],
  pagination: {
    page: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 1,
  },
  counts: {},
};

function statusTone(status) {
  return status === "subscribed" ? "good" : "neutral";
}

export default function Newsletter() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("subscribed");
  const { page, pageSize, setPage, setPageSize, resetPage } = useServerPagination(25);
  const { dismiss, toast, toasts } = useToast();

  const loadSubscribers = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(pageSize),
      status,
      search,
    });

    return api.get(`/newsletter/subscribers?${params.toString()}`);
  }, [page, pageSize, search, status]);

  const { data, error, loading, reload, setData } = useAdminResource(loadSubscribers, emptyPayload);
  const subscribers = data.subscribers || [];
  const counts = data.counts || {};
  const pagination = useMemo(() => {
    const apiPagination = data.pagination || {};

    return {
      page,
      pageSize,
      totalItems: Number(apiPagination.totalItems || 0),
      totalPages: Number(apiPagination.totalPages || 1),
      hasPrevPage: Boolean(apiPagination.hasPrevPage),
      hasNextPage: Boolean(apiPagination.hasNextPage),
      disabled: loading,
      setPage,
      setPageSize,
    };
  }, [data.pagination, loading, page, pageSize, setPage, setPageSize]);

  const updateSubscriber = async (subscriber, nextStatus) => {
    try {
      const result = await api.patch(`/newsletter/subscribers/${subscriber._id}`, { status: nextStatus });
      setData((current) => ({
        ...current,
        subscribers: (current.subscribers || []).map((item) => (
          item._id === result.subscriber._id ? result.subscriber : item
        )),
      }));
      toast(`Subscriber marked ${nextStatus}`, "success");
      reload();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const applySearch = (value) => {
    setSearch(value);
    resetPage();
  };

  const applyStatus = (value) => {
    setStatus(value);
    resetPage();
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Newsletter</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Subscriber list</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            View emails collected from the website footer newsletter signup and manage subscription status.
          </p>
          {error && !loading && <p className="mt-2 text-xs font-black text-amber-600">Using local fallback because subscribers could not be loaded.</p>}
        </div>
        <WorkflowToolbar actions={[{ label: "Refresh", onClick: reload }]} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <InsightCard title="Subscribed" value={counts.subscribed || 0} note="Active newsletter contacts" tone="good" />
        <InsightCard title="Unsubscribed" value={counts.unsubscribed || 0} note="Paused contacts" tone="warn" />
        <InsightCard title="Showing" value={pagination.totalItems} note="Current filtered results" />
      </div>

      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="relative">
            <Search className="absolute left-4 top-3.5 text-gray-400" size={16} />
            <input
              className="w-full rounded-xl border border-gray-200 px-4 py-3 pl-11 text-sm font-semibold outline-none focus:border-[#4391d1]"
              onChange={(event) => applySearch(event.target.value)}
              placeholder="Search subscriber email..."
              value={search}
            />
          </div>
          <select
            className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold"
            onChange={(event) => applyStatus(event.target.value)}
            value={status}
          >
            <option value="subscribed">Subscribed</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="all">All Status</option>
          </select>
        </div>
      </section>

      <section className="rounded-2xl bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-gray-100 p-6">
          <MailCheck className="text-[#4391d1]" />
          <h2 className="text-xl font-black text-gray-900">Newsletter Contacts</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-left text-sm">
            <thead>
              <tr className="text-xs font-black uppercase text-gray-400">
                {["Email", "Status", "Source", "Subscribed", "Last Updated", "Actions"].map((column) => (
                  <th className="px-6 py-3" key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td className="px-6 py-8 text-sm font-bold text-gray-500" colSpan={6}>Loading subscribers...</td>
                </tr>
              ) : subscribers.length ? (
                subscribers.map((subscriber) => (
                  <tr key={subscriber._id}>
                    <td className="px-6 py-4 font-black text-gray-900">{subscriber.email}</td>
                    <td className="px-6 py-4"><StatusPill tone={statusTone(subscriber.status)}>{subscriber.status}</StatusPill></td>
                    <td className="px-6 py-4 font-semibold capitalize text-gray-600">{subscriber.source || "footer"}</td>
                    <td className="px-6 py-4 font-semibold text-gray-500">{formatUsDateTimeWithZone(subscriber.lastSubscribedAt || subscriber.createdAt)}</td>
                    <td className="px-6 py-4 font-semibold text-gray-500">{formatUsDateTimeWithZone(subscriber.updatedAt)}</td>
                    <td className="px-6 py-4">
                      {subscriber.status === "subscribed" ? (
                        <button
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => updateSubscriber(subscriber, "unsubscribed")}
                          type="button"
                        >
                          <UserMinus size={14} /> Unsubscribe
                        </button>
                      ) : (
                        <button
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={() => updateSubscriber(subscriber, "subscribed")}
                          type="button"
                        >
                          <UserRoundCheck size={14} /> Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-6 py-8 text-sm font-bold text-gray-500" colSpan={6}>No newsletter subscribers match this filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <PaginationControls pagination={pagination} />
      </section>
    </div>
  );
}
